import {
  MENU_MAX_DISHES,
  MENU_MAX_DISHES_PER_SECTION,
  MENU_MAX_SECTIONS,
} from '@pulsify/shared/dto'
import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react'
import * as m from '~/paraglide/messages'
import { Button } from '~/shared/ui/button'
import { Input } from '~/shared/ui/input'
import {
  addSection,
  type FullMenuDraft,
  fullMenuRowCount,
  moveSection,
  removeSection,
  renameSection,
  updateSectionDishes,
} from '../lib/menu-editor-model'
import { DishRows } from './dish-rows'

interface FullMenuEditorProps {
  draft: FullMenuDraft
  onChange: (draft: FullMenuDraft) => void
}

export function FullMenuEditor({ draft, onChange }: FullMenuEditorProps) {
  const canAddDish = fullMenuRowCount(draft) < MENU_MAX_DISHES

  return (
    <div className="flex flex-col gap-4">
      {draft.sections.length === 0 ? (
        <p className="border-t py-6 text-muted-foreground text-sm">{m.menuEditorNoSections()}</p>
      ) : (
        <ol className="flex flex-col gap-4">
          {draft.sections.map((section, index) => {
            const name = section.title.trim() || m.menuEditorUntitledSection()
            return (
              <li key={section.key} className="flex flex-col gap-2 rounded-lg border p-4">
                <div className="flex items-center gap-1">
                  <Input
                    aria-label={m.menuEditorSectionTitle()}
                    value={section.title}
                    maxLength={80}
                    placeholder={m.menuEditorSectionTitlePlaceholder()}
                    onChange={(event) =>
                      onChange(renameSection(draft, section.key, event.target.value))
                    }
                    className="mr-2 font-medium"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={m.menuEditorMoveUp({ name })}
                    disabled={index === 0}
                    onClick={() => onChange(moveSection(draft, section.key, -1))}
                  >
                    <ChevronUp />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={m.menuEditorMoveDown({ name })}
                    disabled={index === draft.sections.length - 1}
                    onClick={() => onChange(moveSection(draft, section.key, 1))}
                  >
                    <ChevronDown />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={m.menuEditorRemoveItem({ name })}
                    onClick={() => onChange(removeSection(draft, section.key))}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 />
                  </Button>
                </div>
                <DishRows
                  dishes={section.dishes}
                  limit={MENU_MAX_DISHES_PER_SECTION}
                  canAdd={canAddDish}
                  onChange={(dishes) =>
                    onChange(updateSectionDishes(draft, section.key, () => dishes))
                  }
                />
              </li>
            )
          })}
        </ol>
      )}
      <Button
        type="button"
        variant="secondary"
        className="w-fit"
        disabled={draft.sections.length >= MENU_MAX_SECTIONS}
        onClick={() => onChange(addSection(draft))}
      >
        <Plus />
        {m.menuEditorAddSection()}
      </Button>
    </div>
  )
}
